import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  competitionDetailSchema,
  salonFacetsSchema,
  salonSearchResponseSchema,
  type Salon,
} from '@competa/shared';
import { apiRequest } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import { existingCompetitionId } from '../competitions/CompetitionsPage';
import styles from './Salons.module.css';

/** Anzahl der Treffer je Seite. */
const PAGE_SIZE = 25;

/** Filterfelder, die im URL-Query-String stehen (Deeplink und Browser-Zurück funktionieren). */
const FILTER_KEYS = ['q', 'country', 'from', 'to', 'kind', 'year'] as const;

/**
 * Formatiert ein reines Datum `JJJJ-MM-TT` lokalisiert (ohne Zeitzonenverschiebung).
 * @param iso Datum oder `null`.
 * @param language Sprachcode für die Darstellung.
 * @returns Lokalisiertes Datum oder leerer Text.
 */
export function formatDate(iso: string | null, language: string): string {
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, month! - 1, day).toLocaleDateString(language);
}

/**
 * Prüft, ob ein Einsendeschluss bereits verstrichen ist (bis einschließlich dem Tag selbst gilt er als offen).
 * @param closing Einsendeschluss `JJJJ-MM-TT` oder `null`.
 * @param today Heutiges Datum (für Tests überschreibbar).
 * @returns `true`, wenn der Termin vor heute liegt.
 */
export function isPast(closing: string | null, today: Date = new Date()): boolean {
  if (!closing) return false;
  const [year, month, day] = closing.split('-').map(Number);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return new Date(year!, month! - 1, day).getTime() < startOfToday.getTime();
}

/** Eigenschaften einer Ergebniszeile. */
interface SalonItemProps {
  salon: Salon;
}

/**
 * Eine Zeile der Trefferliste: Nummer, Name, Einsendeschluss und aufklappbare Einzelheiten.
 * @param props Der anzuzeigende Salon.
 */
function SalonItem({ salon }: SalonItemProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const participate = useMutation({
    mutationFn: () =>
      apiRequest('POST', '/competitions', {
        body: { salonId: salon.id },
        schema: competitionDetailSchema,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
      navigate(`/competitions/${created.id}`);
    },
    onError: (error) => {
      // Die Teilnahme gibt es schon: direkt dorthin wechseln.
      const existing = existingCompetitionId(error);
      if (existing) navigate(`/competitions/${existing}`);
    },
  });
  const detailId = `salon-${salon.id}-details`;
  const past = isPast(salon.closingDate);
  return (
    <li className={styles.item}>
      <div className={styles.row}>
        <span className={styles.number}>{salon.fiapNumber}</span>
        <span className={styles.name}>{salon.name}</span>
        <span className={styles.meta}>
          {salon.country && <span>{salon.country}</span>}
          {salon.closingDate ? (
            <span>
              {t('salons.closing')}: {formatDate(salon.closingDate, i18n.language)}
            </span>
          ) : (
            <span>{t('salons.noClosing')}</span>
          )}
          {past && <Badge tone="warning">{t('salons.closed')}</Badge>}
        </span>
        {/* Nach dem Einsendeschluss ist keine Einreichung mehr möglich; die Teilnahme lässt sich aber nachträglich erfassen. */}
        <Button
          variant={past ? 'secondary' : 'primary'}
          disabled={participate.isPending}
          aria-label={t(past ? 'salons.recordLateFor' : 'salons.participateFor', {
            name: salon.name,
          })}
          onClick={() => setConfirming(true)}
        >
          {t(past ? 'salons.recordLate' : 'salons.participate')}
        </Button>
        <Button
          variant="secondary"
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t('salons.hideDetails') : t('salons.details')}
        </Button>
      </div>
      <ConfirmDialog
        title={t(past ? 'salons.confirmLateTitle' : 'salons.confirmTitle')}
        message={t(past ? 'salons.confirmLateMessage' : 'salons.confirmMessage', {
          name: salon.name,
        })}
        confirmLabel={t('salons.confirmCreate')}
        isOpen={confirming}
        busy={participate.isPending}
        onConfirm={() => {
          setConfirming(false);
          participate.mutate();
        }}
        onCancel={() => setConfirming(false)}
      />
      {open && (
        <dl id={detailId} className={styles.details}>
          {salon.kind && (
            <>
              <dt>{t('salons.kind')}</dt>
              <dd>{salon.kind}</dd>
            </>
          )}
          {salon.sectionsCount !== null && (
            <>
              <dt>{t('salons.sections')}</dt>
              <dd>{salon.sectionsCount}</dd>
            </>
          )}
          <dt>{t('salons.judging')}</dt>
          <dd>{formatDate(salon.judgingDate, i18n.language) || '–'}</dd>
          <dt>{t('salons.notification')}</dt>
          <dd>{formatDate(salon.notificationDate, i18n.language) || '–'}</dd>
          {salon.categories.length > 0 && (
            <>
              <dt>{t('salons.categories')}</dt>
              <dd>
                <ul className={styles.plainList}>
                  {salon.categories.map((category) => (
                    <li key={category}>{category}</li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          {salon.price && (
            <>
              <dt>{t('salons.price')}</dt>
              <dd>
                <ul className={styles.plainList}>
                  {salon.price.split('\n').map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          {salon.info && (
            <>
              <dt>{t('salons.info')}</dt>
              <dd>{salon.info}</dd>
            </>
          )}
          {salon.email && (
            <>
              <dt>{t('salons.email')}</dt>
              <dd>
                <a href={`mailto:${salon.email}`}>{salon.email}</a>
              </dd>
            </>
          )}
          {salon.homepage && (
            <>
              <dt>{t('salons.website')}</dt>
              <dd>
                <a href={salon.homepage} target="_blank" rel="noreferrer noopener">
                  {salon.homepage}
                </a>
              </dd>
            </>
          )}
        </dl>
      )}
    </li>
  );
}

/**
 * Heutiges Datum als `JJJJ-MM-TT` in Ortszeit.
 * @param now Zeitpunkt (für Tests überschreibbar).
 * @returns Datum als Text.
 */
export function todayIso(now: Date = new Date()): string {
  /** Zahl mit führender Null auf zwei Stellen. */
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Wettbewerbssuche („Wettbewerb wählen"): Filter nach Name/Nummer, Land, Einsendeschluss-Zeitraum, Art und
 * Jahr; die Filter stehen in der Adresse (teilbar, Browser-Zurück funktioniert). Treffer werden seitenweise
 * nachgeladen. Standard: nur Wettbewerbe mit offenem Einsendeschluss (ab heute); die Voreinstellung entfällt,
 * sobald ein Suchtext oder ein Jahr gewählt ist oder das Feld „Einsendeschluss ab" bewusst geleert wird.
 */
export function SalonSearchPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [pages, setPages] = useState(1);

  const filters = Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) ?? '']));
  // Voreinstellung: nur offene Wettbewerbe. Ein ausdrücklich geleertes Feld (`from=`) zeigt auch vergangene.
  const defaultFromActive = !params.has('from') && !filters.q && !filters.year;
  if (defaultFromActive) filters.from = todayIso();
  const queryString = (() => {
    const search = new URLSearchParams();
    for (const key of FILTER_KEYS) if (filters[key]) search.set(key, filters[key]!);
    search.set('limit', String(PAGE_SIZE * pages));
    return search.toString();
  })();

  const facets = useQuery({
    queryKey: ['salons', 'facets'],
    queryFn: () => apiRequest('GET', '/salons/facets', { schema: salonFacetsSchema }),
    staleTime: 5 * 60_000,
  });
  const results = useQuery({
    queryKey: ['salons', 'search', queryString],
    queryFn: () =>
      apiRequest('GET', `/salons?${queryString}`, { schema: salonSearchResponseSchema }),
    placeholderData: keepPreviousData,
  });

  /**
   * Setzt einen Filter in der Adresse und beginnt wieder mit der ersten Seite.
   * @param key Filtername.
   * @param value Neuer Wert (leer entfernt den Filter).
   */
  function setFilter(key: (typeof FILTER_KEYS)[number], value: string): void {
    const next = new URLSearchParams(params);
    // Das Datumsfeld bleibt bei Leerung als `from=` stehen, damit die Voreinstellung (ab heute) nicht zurückkehrt.
    if (value || key === 'from') next.set(key, value);
    else next.delete(key);
    setPages(1);
    setParams(next, { replace: true });
  }

  const data = results.data;
  const hasMore = data ? data.items.length < data.total : false;
  const hasFilter = FILTER_KEYS.some((key) => (key === 'from' ? params.has('from') : filters[key]));

  return (
    <section>
      <h1>{t('salons.title')}</h1>
      <p>{t('salons.intro')}</p>
      {defaultFromActive && <p className={styles.hint}>{t('salons.defaultHint')}</p>}

      <form
        className={styles.filters}
        role="search"
        aria-label={t('salons.filters')}
        onSubmit={(event) => event.preventDefault()}
      >
        <TextField
          label={t('salons.search')}
          type="search"
          value={filters.q}
          placeholder={t('salons.searchPlaceholder')}
          onChange={(event) => setFilter('q', event.target.value)}
        />
        <div className={styles.selectField}>
          <label htmlFor="filter-country">{t('salons.country')}</label>
          <select
            id="filter-country"
            className={styles.select}
            value={filters.country}
            onChange={(event) => setFilter('country', event.target.value)}
          >
            <option value="">{t('salons.anyCountry')}</option>
            {facets.data?.countries.map((country) => (
              <option key={country.name} value={country.name}>
                {country.name} ({country.count})
              </option>
            ))}
          </select>
        </div>
        <TextField
          label={t('salons.closingFrom')}
          type="date"
          value={filters.from}
          onChange={(event) => setFilter('from', event.target.value)}
        />
        <TextField
          label={t('salons.closingTo')}
          type="date"
          value={filters.to}
          onChange={(event) => setFilter('to', event.target.value)}
        />
        <div className={styles.selectField}>
          <label htmlFor="filter-kind">{t('salons.kind')}</label>
          <select
            id="filter-kind"
            className={styles.select}
            value={filters.kind}
            onChange={(event) => setFilter('kind', event.target.value)}
          >
            <option value="">{t('salons.anyKind')}</option>
            {facets.data?.kinds.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.selectField}>
          <label htmlFor="filter-year">{t('salons.year')}</label>
          <select
            id="filter-year"
            className={styles.select}
            value={filters.year}
            onChange={(event) => setFilter('year', event.target.value)}
          >
            <option value="">{t('salons.anyYear')}</option>
            {facets.data?.years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        {hasFilter && (
          <Button
            variant="secondary"
            className={styles.reset}
            onClick={() => {
              setPages(1);
              setParams({}, { replace: true });
            }}
          >
            {t('salons.reset')}
          </Button>
        )}
      </form>

      {results.isPending && <p role="status">{t('common.loading')}</p>}
      {results.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, results.error)}
        </p>
      )}
      {data && (
        <>
          <p role="status" aria-live="polite">
            {data.total === 0 ? t('salons.empty') : t('salons.results', { count: data.total })}
          </p>
          <ul className={styles.list}>
            {data.items.map((salon) => (
              <SalonItem key={salon.id} salon={salon} />
            ))}
          </ul>
          {hasMore && (
            <Button variant="secondary" onClick={() => setPages((value) => value + 1)}>
              {t('salons.loadMore')}
            </Button>
          )}
        </>
      )}
    </section>
  );
}
